import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ABTestStore, ABTestAssignment } from '~/features/abTests/types';

const AB_TEST_STORAGE_KEY = 'ab-test-store';

// -------------------------------------------------
/**
 * часть пользователей закэшировала по тесту segment_field
 * variant: LEGACY_SEGMENT_FIELD_VARIANTS в localStorage. обновляем версию до актуальной
 *
 * TODO(temp): выпилить можно после 15.09 - у всех пользователей оставшихся на v2 версии 
 * черновик будет помечен как deleted => обновится draft id сразу подтянется актуальная версия без миграций
 */
const LEGACY_SEGMENT_FIELD_VARIANTS = ['v1','segment_default', 'segment_v1', 'segment_v2', 'v2'];

const migrateSegmentFieldAssignmentSync = () => {
	if (typeof window === 'undefined') return;

	try {
		const raw = window.localStorage.getItem(AB_TEST_STORAGE_KEY);
		if (!raw) return;

		const parsed = JSON.parse(raw);
		const seg = parsed?.state?.assignments?.segment_field;

		if (seg?.variant && LEGACY_SEGMENT_FIELD_VARIANTS.includes(seg.variant)) {
			parsed.state.assignments.segment_field = {
				...seg,
				variant: 'v2_segment',
				weight: 100,
			};
			window.localStorage.setItem(AB_TEST_STORAGE_KEY, JSON.stringify(parsed));
		}
	} catch {
	}
};

migrateSegmentFieldAssignmentSync();

// -------------------------------------------------

/**
 * Распределить пользователя по вариантам на основе weights
 * Пример: variants=[{weight: 70}, {weight: 10}, {weight: 10}, {weight: 10}]
 */
function distributeUser(weights: number[]): number {
	const random = Math.random() * 100;
	let accumulated = 0;

	for (let i = 0; i < weights.length; i++) {
		accumulated += weights[i];
		if (random <= accumulated) {
			return i;
		}
	}

	return weights.length - 1; // fallback на последний вариант
}

export const useABTestStore = create<ABTestStore>()(
	persist(
		(set: any, get: any) => ({
			assignments: {},

			setAssignment: (testId: string, variant: string, weight: number) => {
				set((state: ABTestStore) => ({
					assignments: {
						...state.assignments,
						[testId]: {
							testId,
							variant,
							weight,
							assignedAt: Date.now(),
						},
					},
				}));
			},

			getAssignment: (testId: string) => {
				return get().assignments[testId] || null;
			},

			getAllAssignments: () => {
				return get().assignments;
			},

			clearAll: () => {
				set({ assignments: {} });
			},
		}),
		{
			name: AB_TEST_STORAGE_KEY,
			version: 1,
			// -------------------------------------------------
			/**
			 * часть пользователей закэшировала по тесту segment_field
			 * variant: LEGACY_SEGMENT_FIELD_VARIANTS в localStorage. обновляем версию до актуальной
			 *
			 * TODO(temp): выпилить можно после 15.09 - у всех пользователей оставшихся на v2 версии 
			 * черновик будет помечен как deleted => обновится draft id сразу подтянется актуальная версия без миграций
			 */
			migrate: (persistedState: unknown, version: number) => {
				const state = persistedState as { assignments?: Record<string, ABTestAssignment> };
				const seg = state.assignments?.segment_field;

				if (version < 1 && state.assignments && seg && LEGACY_SEGMENT_FIELD_VARIANTS.includes(seg.variant)) {
					state.assignments.segment_field = {
						...seg,
						variant: 'v2_segment',
						weight: 100,
					};
				}

				return state;
			},
			// -------------------------------------------------
		}
	)
);

/**
 * Резолвер для получения варианта теста
 * Приоритет:
 * 1. Query параметр (например, ?testId=variant_name)
 * 2. localStorage (сохраненный вариант)
 * 3. Новое распределение по weights
 */
export function resolveTestVariant(
	testId: string,
	variants: { name: string; weight: number }[],
	queryParams?: Record<string, string>
): string {
	// 1. Проверяем query параметры
	if (queryParams?.[testId]) {
		const variantName = queryParams[testId];
		if (variants.some((v) => v.name === variantName)) {
			return variantName;
		}
	}

	// 2. Проверяем localStorage
	const stored = useABTestStore.getState().getAssignment(testId);
	if (stored) {
		return stored.variant;
	}

	// 3. Распределяем нового пользователя
	const weights = variants.map((v) => v.weight);
	const variantIndex = distributeUser(weights);
	const selectedVariant = variants[variantIndex];

	// Сохраняем в store
	useABTestStore.getState().setAssignment(testId, selectedVariant.name, selectedVariant.weight);

	return selectedVariant.name;
}
